import type { PublicRecord } from '@/types/publicRecords';
import { hasProofCard } from '@/lib/recordTrust';

const U = (id: string, w = 900) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=85`;

/** Heritage-style photography for public archive, geospatial timeline, and map UI */
export const HERITAGE_MEDIA = {
  antiqueMap: U('photo-1578662996442-bb345fd60f4a', 1400),
  clockGears: U('photo-1581091226824-a1641ec7f1bc', 1000),
  artisanHands: U('photo-1455849318740-ab444dd08d09', 1000),
  lighthouse: U('photo-1519681393784-d120267933ba', 1000),
  skyline: U('photo-1486406146926-c627a92fdacb', 1200),
  heritageHall: U('photo-1566127444979-b3d2b14d3f7f', 1200),
  summit: U('photo-1521737852567-6949f5f9f090', 1000),
  community: U('photo-1529156069898-49953e39b3ac', 1000),
  coastline: U('photo-1506905925346-21bda4d32df4', 1200),
  forestTrail: U('photo-1469474968028-56623f02e42e', 1000),
  museum: U('photo-1541961017774-22349ad4a126', 1000),
} as const;

const RECORD_IMAGE_BY_ID: Record<string, string> = {
  'rec-001': HERITAGE_MEDIA.skyline,
  'rec-002': HERITAGE_MEDIA.summit,
  'rec-003': HERITAGE_MEDIA.antiqueMap,
  'rec-004': HERITAGE_MEDIA.community,
  'rec-005': HERITAGE_MEDIA.heritageHall,
  'rec-006': HERITAGE_MEDIA.coastline,
  'rec-007': HERITAGE_MEDIA.museum,
  'rec-008': HERITAGE_MEDIA.lighthouse,
  'rec-009': HERITAGE_MEDIA.artisanHands,
};

const PILLAR_FALLBACK: Record<string, string> = {
  History: HERITAGE_MEDIA.antiqueMap,
  Achievement: HERITAGE_MEDIA.summit,
  Performance: HERITAGE_MEDIA.skyline,
  Hero: HERITAGE_MEDIA.community,
  Mischief: HERITAGE_MEDIA.community,
  Favorites: HERITAGE_MEDIA.artisanHands,
  'Skill / Legacy': HERITAGE_MEDIA.heritageHall,
};

/** Stable cover photo for a public record (timeline, map, archive cards) */
export function getPublicRecordImage(record: PublicRecord): string {
  if (record.imageUrl) return record.imageUrl;
  if (RECORD_IMAGE_BY_ID[record.id]) return RECORD_IMAGE_BY_ID[record.id];
  if (record.recordType === 'heritage_archive' || record.recordType === 'cultural_collection') {
    return HERITAGE_MEDIA.heritageHall;
  }
  if (hasProofCard(record)) return HERITAGE_MEDIA.museum;
  if (record.ownerType === 'institution') return HERITAGE_MEDIA.skyline;
  return PILLAR_FALLBACK[record.pillar] ?? HERITAGE_MEDIA.forestTrail;
}
